// components/WorkExperience.jsx
import React, { useState } from "react";
import {
  FaPlus,
  FaTrash,
  FaUpload,
  FaBuilding,
  FaBriefcase,
  FaCalendarAlt,
  FaDollarSign,
  FaFile,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

export default function WorkExperience({
  data,
  update,
  nextStep,
  prevStep, // ✅ Add prevStep prop
  isLastStep,
  onFinalSubmit,
}) {
  const [submitError, setSubmitError] = useState("");

  // ✅ Safe function normalization
  const safeNextStep = typeof nextStep === "function" ? nextStep : () => {};
  const safePrevStep = typeof prevStep === "function" ? prevStep : () => {};
  const safeFinalSubmit =
    typeof onFinalSubmit === "function" ? onFinalSubmit : () => {};

  const safeData = Array.isArray(data) ? data : [];

  // Add a new empty work experience object
  const addExperience = () => {
    const newId =
      safeData.length > 0 ? Math.max(...safeData.map((w) => w.id)) + 1 : 1;
    update([
      ...safeData,
      {
        id: newId,
        company: "",
        designation: "",
        fromDate: "",
        toDate: "",
        lastSalary: "",
        documents: [],
        isDropdownOpen: false,
        documentSelections: [],
      },
    ]);
  };

  // ✅ Allow deletion of ANY entry, including when there's only one
  const removeExperience = (id) => {
    update(safeData.filter((item) => item.id !== id));
  };

  // Handle text input changes
  const handleChange = (id, field, value) => {
    update(
      safeData.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  // Toggle dropdown
  const toggleDropdown = (id) => {
    update(
      safeData.map((item) =>
        item.id === id
          ? { ...item, isDropdownOpen: !item.isDropdownOpen }
          : item
      )
    );
  };

  // Toggle document checkbox
  const toggleDocumentCheckbox = (id, docType) => {
    update(
      safeData.map((item) => {
        if (item.id === id) {
          const currentSelections = item.documentSelections || [];
          const index = currentSelections.indexOf(docType);

          let newSelections;
          if (index > -1) {
            newSelections = currentSelections.filter((doc) => doc !== docType);
          } else {
            newSelections = [...currentSelections, docType];
          }

          return {
            ...item,
            documentSelections: newSelections,
            isDropdownOpen: true,
          };
        }
        return item;
      })
    );
  };

  // Handle file upload for specific document type
  const handleFileChange = (id, e, docType) => {
    const uploadedFiles = Array.from(e.target.files);
    if (uploadedFiles.length === 0) return;

    update(
      safeData.map((item) => {
        if (item.id === id) {
          const newDocuments = uploadedFiles.map((file) => ({
            id: `${Date.now()}-${Math.random()}`,
            type: docType,
            file: file,
            name: file.name,
            size: file.size,
            uploadedAt: new Date().toISOString(),
          }));

          return {
            ...item,
            documents: [...(item.documents || []), ...newDocuments],
          };
        }
        return item;
      })
    );
  };

  // Remove a specific file
  const removeFile = (id, fileId) => {
    update(
      safeData.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            documents: (item.documents || []).filter(
              (doc) => doc.id !== fileId
            ),
          };
        }
        return item;
      })
    );
  };

  // Document options - ✅ MATCHES BACKEND MAPPING
  const documentOptions = [
    "Joining Letter",
    "Experience Letter",
    "Salary Slip",
    "Offer Letter",
    "Relieving Letter",
    "Appointment Letter",
    "Resignation Letter",
  ];

  // Get files by document type
  const getFilesByType = (documents, docType) => {
    return (documents || []).filter((doc) => doc.type === docType);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Validation function
  const validateWorkExperience = () => {
    setSubmitError("");

    if (safeData.length === 0) {
      return true; // Work experience is optional
    }

    for (const work of safeData) {
      if (!work.company?.trim()) {
        setSubmitError("Please enter company name for all work experiences.");
        return false;
      }
      if (!work.designation?.trim()) {
        setSubmitError("Please enter designation for all work experiences.");
        return false;
      }
      if (!work.fromDate) {
        setSubmitError("Please enter from date for all work experiences.");
        return false;
      }
      if (!work.toDate) {
        setSubmitError("Please enter to date for all work experiences.");
        return false;
      }
      if (work.fromDate && work.toDate && work.fromDate > work.toDate) {
        setSubmitError("From date cannot be after to date.");
        return false;
      }
      // ✅ NEW: Check that at least ONE document is uploaded
      if (!work.documents || work.documents.length === 0) {
        setSubmitError(
          "Please upload at least one document for all work experiences."
        );
        return false;
      }
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateWorkExperience()) {
      return;
    }

    if (isLastStep) {
      safeFinalSubmit();
    } else {
      safeNextStep();
    }
  };

  return (
    <div className="space-y-8 mt-4">
      {safeData.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
          <FaBuilding className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg font-medium">
            No work experience added
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Click "Add work experience" to begin
          </p>
          <p className="text-gray-400 text-xs mt-1">
            (This section is optional)
          </p>
        </div>
      ) : (
        safeData.map((work, index) => (
          <div
            key={work.id}
            className="p-6 bg-[#F9F9F9] rounded-[20px] border border-gray-100 hover:border-gray-200 transition-colors"
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Work Experience {index + 1}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Fill details for this work experience
                </p>
              </div>
              {safeData.length > 0 && (
                <button
                  onClick={() => removeExperience(work.id)}
                  className="text-red-400 hover:text-red-600 p-2 hover:bg-red-50 rounded-full transition-colors"
                  title="Remove this entry"
                >
                  <FaTrash size={14} />
                </button>
              )}
            </div>

            <div className="space-y-6">
              {/* Company & Designation */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm text-[#666666] font-medium block">
                    Company Name*
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={work.company || ""}
                      onChange={(e) =>
                        handleChange(work.id, "company", e.target.value)
                      }
                      placeholder="Enter Company Name"
                      className="w-full h-12 pl-10 pr-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] focus:outline-none transition-all text-gray-700"
                    />
                    <FaBuilding className="absolute left-3.5 top-3.5 text-gray-400 w-4 h-4" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-[#666666] font-medium block">
                    Designation*
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={work.designation || ""}
                      onChange={(e) =>
                        handleChange(work.id, "designation", e.target.value)
                      }
                      placeholder="Enter Your Designation"
                      className="w-full h-12 pl-10 pr-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] focus:outline-none transition-all text-gray-700"
                    />
                    <FaBriefcase className="absolute left-3.5 top-3.5 text-gray-400 w-4 h-4" />
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm text-[#666666] font-medium block">
                    From Date*
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={work.fromDate || ""}
                      onChange={(e) =>
                        handleChange(work.id, "fromDate", e.target.value)
                      }
                      className="w-full h-12 pl-10 pr-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] focus:outline-none transition-all text-gray-700"
                      max={new Date().toISOString().split("T")[0]}
                    />
                    <FaCalendarAlt className="absolute left-3.5 top-3.5 text-gray-400 w-4 h-4" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-[#666666] font-medium block">
                    To Date*
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={work.toDate || ""}
                      onChange={(e) =>
                        handleChange(work.id, "toDate", e.target.value)
                      }
                      className="w-full h-12 pl-10 pr-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] focus:outline-none transition-all text-gray-700"
                      max={new Date().toISOString().split("T")[0]}
                    />
                    <FaCalendarAlt className="absolute left-3.5 top-3.5 text-gray-400 w-4 h-4" />
                  </div>
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id={`currentlyWorking-${work.id}`}
                      checked={work.toDate === ""}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleChange(work.id, "toDate", "");
                        }
                      }}
                      className="w-4 h-4 text-[#0CD974] rounded border-gray-300"
                    />
                    <label
                      htmlFor={`currentlyWorking-${work.id}`}
                      className="ml-2 text-sm text-gray-600"
                    >
                      Currently working here
                    </label>
                  </div>
                </div>
              </div>

              {/* Salary */}
              <div className="space-y-2">
                <label className="text-sm text-[#666666] font-medium block">
                  Monthly CTC (Optional)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={work.lastSalary || ""}
                    onChange={(e) =>
                      handleChange(work.id, "lastSalary", e.target.value)
                    }
                    placeholder="Enter your last salary (e.g., ₹50,000)"
                    className="w-full h-12 pl-10 pr-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] focus:outline-none transition-all text-gray-700"
                  />
                  <FaDollarSign className="absolute left-3.5 top-3.5 text-gray-400 w-4 h-4" />
                </div>
              </div>

              {/* Document Selection */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-[#666666] font-medium block">
                    Select Documents to Upload (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => toggleDropdown(work.id)}
                    className="w-full flex items-center justify-between p-4 rounded-[10px] bg-white border border-gray-200 hover:border-[#0CD974] transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 rounded bg-[#0CD974]/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-[#0CD974]">
                          {(work.documentSelections || []).length}
                        </span>
                      </div>
                      <span className="text-gray-700">
                        {(work.documentSelections || []).length === 0
                          ? "Select document types"
                          : `${(work.documentSelections || []).length} document types selected`}
                      </span>
                    </div>
                    {work.isDropdownOpen ? (
                      <FaChevronUp className="text-gray-400" />
                    ) : (
                      <FaChevronDown className="text-gray-400" />
                    )}
                  </button>
                </div>

                {work.isDropdownOpen && (
                  <div className="bg-white border border-gray-200 rounded-[10px] p-4">
                    <div className="space-y-3">
                      {documentOptions.map((docType) => {
                        const isSelected = (
                          work.documentSelections || []
                        ).includes(docType);
                        return (
                          <div
                            key={docType}
                            className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              id={`${work.id}-${docType}`}
                              checked={isSelected}
                              onChange={() =>
                                toggleDocumentCheckbox(work.id, docType)
                              }
                              className="w-4 h-4 text-[#0CD974] rounded border-gray-300 focus:ring-[#0CD974] focus:ring-2"
                            />
                            <label
                              htmlFor={`${work.id}-${docType}`}
                              className="flex-1 text-sm text-gray-700 cursor-pointer select-none"
                            >
                              {docType}
                            </label>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => toggleDropdown(work.id)}
                        className="w-full py-2 text-sm font-medium text-[#0CD974] hover:bg-[#0CD974]/5 rounded-lg transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}

                {/* Upload Sections */}
                {(work.documentSelections || []).length > 0 && (
                  <div className="space-y-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700">
                      Upload Documents
                    </h4>

                    <div className="space-y-4">
                      {(work.documentSelections || []).map((docType) => {
                        const filesForType = getFilesByType(
                          work.documents || [],
                          docType
                        );

                        return (
                          <div key={docType} className="space-y-3">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium text-gray-700">
                                {docType}
                                {filesForType.length > 0 && (
                                  <span className="ml-2 text-xs text-gray-500">
                                    ({filesForType.length} uploaded)
                                  </span>
                                )}
                              </label>
                            </div>

                            <div className="space-y-2">
                              <input
                                type="file"
                                key={`input-${work.id}-${docType}`}
                                id={`file-${work.id}-${docType.replace(/\s+/g, "-")}`}
                                className="hidden"
                                multiple
                                onChange={(e) =>
                                  handleFileChange(work.id, e, docType)
                                }
                              />
                              <label
                                htmlFor={`file-${work.id}-${docType.replace(/\s+/g, "-")}`}
                                className="flex items-center justify-center gap-2 w-full h-12 rounded-[10px] border border-dashed border-gray-300 bg-white cursor-pointer hover:border-[#0CD974] hover:bg-green-50 transition-all"
                              >
                                <FaUpload className="text-gray-400" />
                                <span className="text-sm text-gray-500">
                                  Upload {docType}
                                </span>
                              </label>
                            </div>

                            {filesForType.length > 0 && (
                              <div className="space-y-2">
                                {filesForType.map((doc) => (
                                  <div
                                    key={doc.id}
                                    className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200"
                                  >
                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                      <FaFile className="text-gray-400 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-800 truncate">
                                          {doc.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {formatFileSize(doc.size)} •{" "}
                                          {new Date(
                                            doc.uploadedAt
                                          ).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeFile(work.id, doc.id)
                                      }
                                      className="text-red-400 hover:text-red-600 p-1 hover:bg-red-50 rounded-full ml-2 flex-shrink-0"
                                    >
                                      <FaTimes size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      )}

      {/* Add Button */}
      <button
        onClick={addExperience}
        type="button"
        className="w-full flex items-center justify-center gap-2 text-[#0CD974] hover:text-[#0bb963] font-medium py-3 border border-dashed border-[#0CD974] rounded-[10px] hover:bg-green-50 transition-all"
      >
        <FaPlus size={14} />
        <span>Add work experience</span>
      </button>

      {/* ✅ Action Buttons with Previous */}
      <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
        <button
          onClick={safePrevStep}
          className="px-8 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          Previous
        </button>

        <button
          onClick={handleSubmit}
          className="px-8 py-3 rounded-[10px] bg-[#0CD974] text-white font-medium hover:bg-[#0bb963] transition-colors shadow-md"
        >
          {isLastStep ? "Submit All" : "Save & Continue"}
        </button>
      </div>
      {submitError && (
        <div className="fixed bottom-6 left-6 z-50 max-w-sm bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg">
          <p className="text-sm font-medium">{submitError}</p>
        </div>
      )}
    </div>
  );
}
